import log from "loglevel";
import ProjectWorld, { createServerEvent, ProjectEventType, type ShareLink } from "../ProjectWorld";
import type { BaseEvent, BaseProjectionResult, BaseEventInput, EventProcessor } from "@utilitydelta/clientside-event-sourcing";
import { ServerEvent } from "@utilitydelta/eventplanedb-client";

export const MINOR_VERSION: number = 1;

export interface DisableShareLink_LocalEvent extends BaseEvent<ProjectEventType> {
    tp: typeof ProjectEventType.DisableShareLink;
    shareLinkId: string; // ID of the share link to disable
}

export interface DisableShareLink_ProjectionResult extends BaseProjectionResult {
    kind: 'DisableShareLink_ProjectionResult';
    shareLink: ShareLink; // The disabled share link
}

export interface DisableShareLink_EventInputs extends BaseEventInput {
    kind: 'DisableShareLink_EventInputs';
    shareLinkId: string; // ID of the share link to disable
}

export type DisableShareLink_EventProcessorType = EventProcessor<ProjectEventType, DisableShareLink_EventInputs, DisableShareLink_LocalEvent, DisableShareLink_ProjectionResult, DisableShareLink_LocalEvent, ServerEvent<ProjectEventType>>;

export class DisableShareLink_EventProcessor implements DisableShareLink_EventProcessorType {

    private projectWorld: ProjectWorld;
    constructor(pw: ProjectWorld) { this.projectWorld = pw; }

    public get shouldEncrypt(): boolean { return true; }
    public get eventType(): ProjectEventType { return ProjectEventType.DisableShareLink; }
    public get eventTypeMinorVersion(): number { return MINOR_VERSION; }
    public get undoEventType(): ProjectEventType { return ProjectEventType.DisableShareLink; }
    public get undoEventTypeMinorVersion(): number { return MINOR_VERSION; }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public createUndoEvent = (_undoEvent: DisableShareLink_LocalEvent, _originalEventProjectionResult: DisableShareLink_ProjectionResult): boolean => {
        return false;
    }

    public projectOntoWorld = (event: DisableShareLink_LocalEvent, addUpdatedEntitiesToDirtySet: boolean, returnProjectionResults: boolean): DisableShareLink_ProjectionResult | undefined => {
        // Check for invalid event
        if (!event.shareLinkId) {
            log.warn("Invalid DisableShareLink event: missing share code hash", event);
            return undefined;
        }

        const entity = this.projectWorld.shareLinks.maybeGet(event.shareLinkId);
        if (!entity) {
            log.warn("Share link not found", event);
            return undefined;
        }

        // Verify this event is newer than the last processed event of this type
        if (entity.lastEventTime[event.tp] !== undefined && entity.lastEventTime[event.tp] > event.et) {
            return undefined;
        }

        // Update entity fields based on event data
        entity.isActive = false;

        // Update metadata
        entity.lastEventTime[event.tp] = event.et;
        entity.modifiedByClientId = event.ci;
        entity.modifiedByUserId = event.ui;
        entity.modifiedOn = event.et;

        if (addUpdatedEntitiesToDirtySet) {
            this.projectWorld.addEntityToSave('shareLinks', entity);
        }
        return !returnProjectionResults ? undefined : {
            kind: 'DisableShareLink_ProjectionResult',
            shareLink: entity,
        };
    }

    public createLocalEvent = (eventToPopulate: DisableShareLink_LocalEvent, userInputs: DisableShareLink_EventInputs): boolean => {

        if (userInputs.shareLinkId.length === 0) return false;
        const share = this.projectWorld.shareLinks.maybeGet(userInputs.shareLinkId);
        if (share === undefined || !share.isActive) return false;

        eventToPopulate.shareLinkId = userInputs.shareLinkId;

        return true;
    }

    public toServerEvent = async (localEvent: DisableShareLink_LocalEvent): Promise<ServerEvent<ProjectEventType>> => {
        const eventData = {
            shareLinkId: localEvent.shareLinkId
        };
        const eventDataString = JSON.stringify(eventData);
        return createServerEvent(localEvent, eventDataString);
    }

    public fromServerEvent = async (localEventToPopulate: DisableShareLink_LocalEvent, serverEvent: ServerEvent<ProjectEventType>): Promise<void> => {
        const eventData = JSON.parse(atob(serverEvent.ev))
        Object.assign(localEventToPopulate, eventData);
    }
}